import { readQueryParam } from "./readQueryParam";
import type { Log } from "@slot/presentation";

/**
 * Discriminated result of parsing `?backend=` from the page URL.
 *
 * - `local`    — caller resolves to `BootConfig.localhostUrl`.
 * - `deployed` — caller resolves to `BootConfig.deployedUrl` (staging/prod).
 * - `url`      — verbatim http(s):// URL from the query param.
 * - `scripted` — force offline ScriptedRoundProvider (empty effective baseUrl
 *                + fixture launch-context source).
 */
export type BackendBaseUrlOverride =
  | { kind: "local" }
  | { kind: "deployed" }
  | { kind: "url"; value: string }
  | { kind: "scripted" };

/**
 * Reads `?backend=<value>` from the page URL and returns a discriminated
 * override, or null when the param is absent / invalid.
 *
 * Accepted values:
 *   `?backend=local`             → { kind: "local" }    (→ localhostUrl)
 *   `?backend=deployed|staging`  → { kind: "deployed" } (→ deployedUrl; `staging` is an alias)
 *   `?backend=https://...`       → { kind: "url", value: "https://..." }
 *   `?backend=http://...`        → { kind: "url", value: "http://..." }
 *   `?backend=scripted`          → { kind: "scripted" }
 *   anything else                → null + warning log
 *
 * The caller is responsible for gating the override on `devModeEnabled`
 * before applying it. Production builds must ignore this entirely.
 */
export function resolveBackendBaseUrlOverride(
  queryString: string | null,
  log: Log,
): BackendBaseUrlOverride | null {
  const raw = readQueryParam(queryString, "backend");
  if (raw === null) return null;
  if (raw === "local") return { kind: "local" };
  if (raw === "deployed" || raw === "staging") return { kind: "deployed" };
  if (raw === "scripted") return { kind: "scripted" };
  if (/^https?:\/\//.test(raw)) return { kind: "url", value: raw };
  log(
    `[Backend] ignored ?backend="${raw}" — must be "local", "deployed" (alias "staging"), "scripted", or an http(s):// URL`,
  );
  return null;
}
